import { Uuid } from '@/api/types';
import { EmployeeComposed, EmployeeRole } from '@/api/apis/resources';
import { CustomerRoleT, CustomerMT, OpoRoleEnum } from '@/api/apis/customer';

export enum UserBoardFilters {
  organ = 'organ',
  donorType = 'donorType',
  logisticProviderTypes = 'logisticProviderTypes',
  routePlanningStatuses = 'routePlanningStatuses',
  quoteStatuses = 'quoteStatuses',
  groundConfirmationStatuses = 'groundConfirmationStatuses',
  assignedUsers = 'assignedUsers',
  assignedMe = 'assignedMe',
  recent = 'recent',
  favorites = 'favorites',
  duplicates = 'duplicates',
  // activeCases = 'activeCases', // Temp removed in TMDXLP-8017
  trialProgramIds = 'trialProgramIds',
  randomizationResultTypes = 'randomizationResultTypes',
  serviceRegionIds = 'serviceRegionIds',
  txCenterIds = 'txCenterIds',
  opoIds = 'opoIds',
  opoRequest = 'opoRequest',
  isReallocatedFromSource = 'isReallocatedFromSource',
}

export enum UserBoardSettings {
  isRows = 'isRows',
  statusesOrdered = 'statusesOrdered',
  groupsOrdered = 'groupsOrdered',
  showGroups = 'showGroups',
  hideEmpty = 'hideEmpty',
}

export enum UserChatFilters {
  chatOrganTypes = 'chatOrganTypes',
  chatDonorTypes = 'chatDonorTypes',
  chatChatTypes = 'chatChatTypes',
  chatUnreadOnly = 'chatUnreadOnly',
}

export enum UserArchiveFilters {
  archiveOrganType = 'archiveOrganType',
  archiveDonorType = 'archiveDonorType',
  archiveCaseStatuses = 'archiveCaseStatuses',
  archiveOutcomeType = 'archiveOutcomeType',
  archiveCaseOutcome = 'archiveCaseOutcome',
  archiveTxCenterIds = 'archiveTxCenterIds',
  archiveInvoiceStatuses = 'archiveInvoiceStatuses',
  archiveHasInvoice = 'archiveHasInvoice',
  archiveTrialProgramIds = 'archiveTrialProgramIds',
  archiveRandomizationResultFilter = 'archiveRandomizationResultFilter',
  archiveServiceRequestDateTimeFrom = 'archiveServiceRequestDateTimeFrom',
  archiveServiceRequestDateTimeTo = 'archiveServiceRequestDateTimeTo',
  archiveOutcomeDateTimeFrom = 'archiveOutcomeDateTimeFrom',
  archiveOutcomeDateTimeTo = 'archiveOutcomeDateTimeTo',
  archiveOffOCSDateTimeFrom = 'archiveOffOCSDateTimeFrom',
  archiveOffOCSDateTimeTo = 'archiveOffOCSDateTimeTo',
  archiveCustomerCaseStatuses = 'archiveCustomerCaseStatuses',
  archiveServiceRegionIds = 'archiveServiceRegionIds',
  archiveOpoRequest = 'archiveOpoRequest',
  archiveIsReallocatedFromSource = 'archiveIsReallocatedFromSource',
}

export const UserPreferences = {
  ...UserBoardFilters,
  ...UserBoardSettings,
  ...UserChatFilters,
  ...UserArchiveFilters,
};

export type TUserPreferences = (typeof UserPreferences)[keyof typeof UserPreferences];

export type UserPreferenceType = {
  name: TUserPreferences;
  value: string;
};

export enum UserType {
  None = 'None',
  Employee = 'Employee',
  Customer = 'Customer',
  System = 'System',
}

export type UserRole = EmployeeRole | CustomerRoleT;

export type User = {
  id: Uuid;
  employeeId: Uuid;
  photoPath: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  roles: UserRole[];
  type: UserType;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
  userPreferences: UserPreferenceType[];
};

export type OpoOrganizationMembership = {
  organizationId: string;
  organizationName: string;
  organizationCode: string;
  organizationCountryCode?: string;
  isPrimary: boolean;
  roles: OpoRoleEnum[];
  permissions: string[];
};

export type OpoContact = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  photoPath: string | null;
  primaryPhone: string | null;
  secondaryPhone: string | null;
  opoUserInOpoOrganizations: OpoOrganizationMembership[];
  isAcceptedToReceiveNotifications: boolean;
  isAcceptedTermsOfServiceAndPrivacyPolicy: boolean;
};

export enum OpoUserType {
  Customer = 'Customer',
  OpoUser = 'OpoUser',
}

export type UserProfileType = {
  isActive: boolean;
  userId: string;
  userType?: string;
  employee: EmployeeComposed;
  customer: CustomerMT | null;
  userPreferences: UserPreferenceType[];
  permissions?: string[];
  opoContact?: OpoContact;
  userTypes?: OpoUserType[];
};

export type UserSettingsType = {
  userId: string;
  twilioId: string;
  email: boolean;
  sms: boolean;
  voice: boolean;
  hasGetStreamAccount: boolean;
};

export type ChangeEmailType = {
  email: string;
};

export type VerifyEmailType = {
  code: string;
};

export type SettingsType = {
  userId: string;
  push: boolean;
  sms: boolean;
  toast: boolean;
};
